/**
 * The shape of a translation dictionary, and the machinery that makes a missing
 * translation a COMPILE error rather than a string of dots on screen.
 *
 * v1 shipped `dashboard.flightTypes.undefined` to production, and a filter option
 * whose label rendered as the literal `dashboard.flightTypes.ALL` because no
 * locale defined it. Neither is possible here: `en` is the source of truth, the
 * other locales are typed against it, and `t()` only accepts a key that exists.
 */

/**
 * A plural set. `other` is required because every language has it and it is the
 * fallback; the rest appear only where a language uses them — Russian needs `few`
 * and `many`, Kazakh does not, and English uses just `one` and `other`. Which
 * form applies is decided by `Intl.PluralRules`, never by comparing the number to
 * 1 by hand.
 *
 * **Never name an ordinary key `other`.** This shape is matched structurally, and
 * extra properties do not disqualify a match — so a group of plain strings that
 * happens to contain `other` IS a `PluralForms`. `TranslationKey` then treats the
 * whole group as a leaf, stops recursing, and every key beneath it silently ceases
 * to exist: `t('airlines.panel.codes')` stops compiling while `t('airlines.panel')`
 * starts. It fails loudly at the call sites rather than at the dictionary, which is
 * a long way from the actual cause. Name such a section `details`, `misc` —
 * anything but `other`.
 */
export interface PluralForms {
  zero?: string;
  one?: string;
  two?: string;
  few?: string;
  many?: string;
  other: string;
}

/** What a dictionary can hold at the end of a branch. */
export type Leaf = string | PluralForms;

/**
 * Every dotted path through a dictionary: `'theme.dark' | 'flights.found' | …`.
 *
 * This is what gives `t()` autocompletion and rejects a typo. It recurses into
 * nested groups and stops at a leaf — a plural set is a destination, not a branch
 * to keep walking.
 */
export type TranslationKey<T> = {
  [K in keyof T & string]: T[K] extends Leaf ? K : `${K}.${TranslationKey<T[K]>}`;
}[keyof T & string];

/** Values substituted into `{placeholders}`. */
export type Interpolations = Record<string, string | number>;

export const LOCALES = ['kk', 'ru', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

/**
 * The tag handed to `Intl` for dates and numbers — not the same thing as the UI
 * locale. `en` alone would format 24 July as 7/24; this product is read in
 * Kazakhstan, where the day comes first.
 */
export const INTL_LOCALE: Record<Locale, string> = {
  kk: 'kk-KZ',
  ru: 'ru-RU',
  en: 'en-GB',
};

/** Shown in the language switcher, each in its own language — never translated. */
export const LOCALE_NAMES: Record<Locale, string> = {
  kk: 'Қазақша',
  ru: 'Русский',
  en: 'English',
};

/** The short form for a compact switcher. */
export const LOCALE_SHORT: Record<Locale, string> = {
  kk: 'ҚАЗ',
  ru: 'РУС',
  en: 'ENG',
};
